import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import {
  getKnowledgeDocumentMimeType,
  MAX_KNOWLEDGE_DOCUMENT_SIZE_BYTES,
} from '@lgg/isomorphic/knowledge-document-file';
import {
  AiAgentsNamespaceCreateDocumentSourceItemArgs,
  AiAgentsNamespaceGenerateDocumentSourceItemUploadPostArgs,
  Mutation,
} from '@lgg/isomorphic/types/__generated__/graphql';
import { useShowNotification } from 'src/components/general/feedback/hooks/use-show-notification';
import {
  CREATE_DOCUMENT_SOURCE_ITEM_MUTATION,
  GENERATE_DOCUMENT_UPLOAD_POST_MUTATION,
} from 'src/components/pages/ai-agents/knowledge-base-sources/documents/queries';
import { useCurrentInstitution } from 'src/hooks/use-current-institution';
import { uploadFileToS3 } from 'src/utils/upload-file-to-s3';

export const useUploadDocuments = ({
  knowledgeBaseId,
  onUploaded,
}: {
  knowledgeBaseId: string;
  onUploaded: VoidFunction;
}) => {
  const { t } = useTranslation(['aiAgents']);
  const { id: companyId } = useCurrentInstitution();
  const showNotification = useShowNotification();
  const [isUploading, setIsUploading] = useState(false);

  const [generateUploadPost] = useMutation<
    Pick<Mutation, 'aiAgents'>,
    AiAgentsNamespaceGenerateDocumentSourceItemUploadPostArgs
  >(GENERATE_DOCUMENT_UPLOAD_POST_MUTATION);

  const [createDocument] = useMutation<
    Pick<Mutation, 'aiAgents'>,
    AiAgentsNamespaceCreateDocumentSourceItemArgs
  >(CREATE_DOCUMENT_SOURCE_ITEM_MUTATION);

  const uploadDocument = useCallback(
    async (file: File) => {
      const fileName = file.name;
      const mimeType = getKnowledgeDocumentMimeType(fileName);
      const showUploadError = (reason: 'invalidType' | 'tooLarge' | 'failed') =>
        showNotification({
          type: 'error',
          title: t('aiAgents:knowledge.documents.upload.errorTitle'),
          message: t(`aiAgents:knowledge.documents.upload.${reason}`, { fileName }),
        });

      if (!mimeType) {
        showUploadError('invalidType');

        return;
      }

      if (file.size > MAX_KNOWLEDGE_DOCUMENT_SIZE_BYTES) {
        showUploadError('tooLarge');

        return;
      }

      try {
        const { data } = await generateUploadPost({
          variables: {
            input: {
              companyId,
              knowledgeBaseId,
              filename: fileName,
              mimeType,
              sizeBytes: file.size,
            },
          },
        });
        const presignedPost = data?.aiAgents.generateDocumentSourceItemUploadPost;
        const sourceUrl =
          presignedPost &&
          (await uploadFileToS3(
            new File([file], fileName, { type: mimeType }),
            presignedPost,
          ));

        if (!sourceUrl) {
          throw new Error('Upload failed');
        }

        await createDocument({
          variables: {
            input: {
              companyId,
              knowledgeBaseId,
              title: fileName,
              fileName,
              sourceUrl,
              mimeType,
              size: file.size,
            },
          },
        });

        showNotification({
          type: 'success',
          title: t('aiAgents:knowledge.documents.upload.success'),
          message: fileName,
        });
      } catch {
        showUploadError('failed');
      }
    },
    [companyId, createDocument, generateUploadPost, knowledgeBaseId, showNotification, t],
  );

  const uploadDocuments = useCallback(
    async (files: File[]) => {
      setIsUploading(true);
      await Promise.all(files.map(uploadDocument));
      setIsUploading(false);
      onUploaded();
    },
    [onUploaded, uploadDocument],
  );

  return { uploadDocuments, isUploading };
};
